<template>
  <div>
    <!-- ── Page header ─────────────────────────────────────── -->
    <div class="vm-page-header">
      <div>
        <h1>{{ greetingWithName }}</h1>
        <span class="vm-page-header__sub">{{ todayFormatted }}</span>
      </div>
      <div
        class="vm-page-header__actions"
        v-if="userIsDoctor || userIsReceptionist"
      >
        <InviteAssistant />
        <router-link to="/resumo-da-agenda" class="btn btn--outline btn--sm">
          Ver agenda
        </router-link>
        <router-link to="/criar-agenda" class="btn btn--accent btn--sm">
          + Novo horário
        </router-link>
      </div>
    </div>

    <div class="db-content">
      <!-- ── Convites de clínica pendentes (médico) ───────── -->
      <ClinicInvitesNotice v-if="userIsDoctor" />

      <!-- ── Métricas (médico / recepcionista) ────────────── -->
      <div class="metrics4" v-if="userIsDoctor || userIsReceptionist">
        <div class="card metric-card">
          <div
            class="metric-icon"
            style="background:var(--color-primary-light);color:var(--color-primary)"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect
                x="3"
                y="4"
                width="18"
                height="17"
                rx="2"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="M3 9h18M8 2v4M16 2v4"
                stroke="currentColor"
                stroke-width="2"
              />
            </svg>
          </div>
          <span class="metric-label">Consultas hoje</span>
          <span class="metric-value">{{ todayAppointments.length }}</span>
        </div>

        <div class="card metric-card">
          <div
            class="metric-icon"
            style="background:var(--color-warning-light);color:var(--color-warning)"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="9"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="M12 7v5l3 2"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <span class="metric-label">Pendentes</span>
          <span class="metric-value">
            {{ scheduledAppointments.length }}
          </span>
        </div>

        <div class="card metric-card">
          <div
            class="metric-icon"
            style="background:var(--color-success-light);color:var(--color-success)"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M20 6L9 17l-5-5"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <span class="metric-label">Realizadas</span>
          <span class="metric-value">
            {{ pastAppointments.length }}
          </span>
        </div>

        <router-link
          to="/financeiro"
          class="card metric-card metric-card--interactive"
        >
          <div
            class="metric-icon"
            style="background:var(--color-accent-light);color:var(--color-accent)"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2v20M17 6H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <span class="metric-label">Receita do dia</span>
          <span class="metric-value" style="font-size:var(--font-size-2xl)">
            {{ intToBRL(todayRevenueCents || 0, true) }}
          </span>
          <span class="db-metric-link">Ver financeiro →</span>
        </router-link>
      </div>

      <!-- ── Próxima consulta / ação de sala ──────────────── -->
      <div
        class="card db-next-card"
        v-if="
          appointments.length && nextAppointment.buttons && !userIsReceptionist
        "
      >
        <div class="db-next-info">
          <div class="avatar avatar--md">
            {{ avatarInitials(nextPatientName || nextDoctorName) }}
          </div>
          <div class="db-next-text">
            <span class="db-next-label">{{ nextAppointmentCaption }}</span>
            <strong class="db-next-name">
              {{ nextPatientName || nextDoctorName }}
            </strong>
            <small class="db-next-countdown" v-if="countDown">
              em {{ countDown }}
            </small>
          </div>
        </div>
        <button
          class="btn btn--primary btn--sm"
          :disabled="
            changeCreateRoomLabelIfRoomExist ===
              'Aguarde o profissional criar a sala'
          "
          @click.prevent="callAction"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <rect
              x="2"
              y="3"
              width="20"
              height="14"
              rx="2"
              stroke="currentColor"
              stroke-width="2"
            />
            <path
              d="M8 21h8M12 17v4"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          {{ changeCreateRoomLabelIfRoomExist }}
        </button>
      </div>

      <!-- ── Tabela de consultas com abas ─────────────────── -->
      <div class="card card--flush">
        <div class="db-tabs-header">
          <div class="tabs">
            <span
              class="tab"
              :class="{ 'tab--active': activeTab === 0 }"
              @click="activeTab = 0"
            >
              Próximas consultas
            </span>
            <span
              class="tab"
              :class="{ 'tab--active': activeTab === 1 }"
              @click="activeTab = 1"
            >
              Histórico
            </span>
            <span
              class="tab"
              :class="{ 'tab--active': activeTab === 2 }"
              @click="activeTab = 2"
            >
              Canceladas
            </span>
            <span
              v-if="userIsDoctor"
              class="tab"
              :class="{ 'tab--active': activeTab === 3 }"
              @click="onAuditTabClick"
            >
              Ações da secretária
            </span>
          </div>
        </div>

        <AppointmentsTable
          v-if="activeTab === 0"
          title="Próximas consultas"
          :data="futureAppointments"
          :fields="tableConfig('future')"
          :apointments-length="futureAppointments.length"
          :show-empty="true"
          empty-text="Nenhuma consulta marcada."
          sort-by="formated_scheduled_at"
          :busy="!isFutureAppointmentsLoaded"
          @appointmentCanceled="appointmentCanceled"
          @appointmentFinished="appointmentFinished"
          @appointmentRoomCreated="appointmentRoomCreated"
          @appointmentRoomLink="copyAppointmentRoomLink"
          @createdRoom="joinCreatedRoom"
        />

        <AppointmentsTable
          v-if="activeTab === 1"
          title="Histórico de consultas"
          :data="pastAppointments"
          :fields="tableConfig()"
          :apointments-length="pastAppointments.length"
          :show-empty="true"
          empty-text="Nenhuma consulta marcada."
          :busy="!isPastAppointmentsLoaded"
          @appointmentCanceled="appointmentCanceled"
        />

        <AppointmentsTable
          v-if="activeTab === 2"
          title="Consultas canceladas"
          :data="canceledAppointments"
          :fields="tableConfig()"
          :apointments-length="canceledAppointments.length"
          :show-empty="true"
          empty-text="Nenhuma consulta marcada."
          :busy="!isCanceledAppointmentsLoaded"
          @appointmentCanceled="appointmentCanceled"
        />

        <div v-if="activeTab === 3" class="audit-log-panel">
          <p v-if="auditLog.length === 0" class="audit-log-panel__empty">
            Nenhuma ação registrada.
          </p>
          <div v-else class="audit-log-table-wrap">
            <table class="audit-log-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Ação</th>
                  <th>Paciente</th>
                  <th>Realizado por</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="entry in pagedAuditLog" :key="entry.id">
                  <td class="audit-log-table__date">
                    {{ formatAuditDate(entry.created_at) }}
                  </td>
                  <td>
                    <span class="badge" :class="actionBadgeClass(entry.action)">
                      {{ actionLabel(entry.action) }}
                    </span>
                  </td>
                  <td>{{ entry.patient && entry.patient.name }}</td>
                  <td>{{ entry.performed_by && entry.performed_by.name }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Rodapé: total e paginação -->
          <div class="at-footer" v-if="auditLog.length">
            <span class="at-footer-count">
              Mostrando
              <b>{{ showingAuditPerPage }}</b>
              de
              <b>{{ auditLog.length }}</b>
              ações
            </span>

            <button
              v-if="showMoreAuditButton"
              class="at-btn at-btn--link"
              @click="seeMoreAuditEntries()"
            >
              Ver mais {{ showedAuditPerPageValue }}
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M5 12h14M13 6l6 6-6 6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>

            <button
              v-else-if="showHideAuditButton"
              class="at-btn at-btn--link"
              @click="hideAllAuditEntries()"
            >
              Esconder resultado
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M5 12h14M11 6l-6 6 6 6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  userComputed,
  userMethods,
  roomMethods,
  doctorMethods,
  appointmentsMethods
} from "./../state/helpers";
import AppointmentsTable from "../components/AppointmentsTable";
import InviteAssistant from "../components/doctor/InviteAssistant.vue";
import ClinicInvitesNotice from "../components/doctor/ClinicInvitesNotice.vue";
import {
  isAfter,
  closestIndexTo,
  parseISO,
  differenceInSeconds,
  formatDistanceToNow,
  format
} from "date-fns";
import { pt as locale } from "date-fns/locale";
import { isDoctor, isPatient, isReceptionist } from "../utils/userRole";
import { stripHonorific } from "../utils/name";
import { Doctor } from "../services/doctor";
import { intToBRL } from "../utils/currency/convert";

export default {
  name: "Dashboard",
  components: {
    AppointmentsTable,
    InviteAssistant,
    ClinicInvitesNotice
  },
  data() {
    return {
      activeTab: 0,
      isBusy: false,
      isFutureAppointmentsLoaded: true,
      isPastAppointmentsLoaded: true,
      isCanceledAppointmentsLoaded: true,
      appointments: [],
      futureAppointments: [],
      apointmentsLength: 0,
      selected: "Próximas consultas",
      doctorTableConfig: [
        { key: "name", label: "paciente" },
        { key: "institution_name", label: "clínicas" },
        { key: "formated_scheduled_at", label: "data" },
        { key: "formated_scheduled_at_time", label: "hora" },
        { key: "status", label: "status" },
        { key: "med_record", label: "ficha" },
        { key: "doctor_actions", label: "ações" }
      ],
      patientTableConfig: [
        { key: "name", label: "doutor" },
        { key: "specialty", label: "especialidade" },
        { key: "formated_scheduled_at", label: "data" },
        { key: "formated_scheduled_at_time", label: "hora" },
        { key: "status", label: "status" },
        { key: "patient_actions", label: "ações" }
      ],
      nextAppointmentHasRoom: false,
      auditLog: [],
      auditPerPage: 5,
      auditAddPerPage: 10,
      todayRevenueCents: null
    };
  },
  mounted() {
    this.isBusy = true;
    this.isFutureAppointmentsLoaded = false;
    this.isPastAppointmentsLoaded = false;
    this.sendFetchAppointments();
    this.sendFetchFutureAppointments();
    this.sendFetchPastAppointments();
    this.sendCanceledAppointments();
    if (this.userIsDoctor) this.loadTodayRevenue();
  },
  updated() {
    if (this.nextAppointment.room && this.nextAppointment.room.id) {
      this.nextAppointmentHasRoom = true;
    }
  },
  methods: {
    ...doctorMethods,
    ...userMethods,
    ...appointmentsMethods,
    ...roomMethods,
    intToBRL,
    loadTodayRevenue() {
      Doctor.earnings()
        .then(res => {
          this.todayRevenueCents = res.data.today_cents;
        })
        .catch(() => {});
    },
    callAction() {
      this.nextAppointmentHasRoom
        ? this.joinRoom(this.nextAppointment.room)
        : this.callCreateRoom({ appointment_id: this.nextAppointment.id });
    },
    sendFetchAppointments() {
      return this.fetchAppointments().then(response => {
        this.isBusy = false;
        this.apointmentsLength = response.data.length;
        this.appointments = response.data.filter(appointment => {
          if (isAfter(parseISO(appointment.scheduled_at), new Date())) {
            if (appointment.room)
              this.getRoomDetailsV2(appointment.room.id).then(respon => {
                appointment.room = respon.data;
              });
            return appointment;
          }
        });
        this.appointments = this.appointments.splice(0, 6);
      });
    },
    sendFetchFutureAppointments() {
      return this.fetchFutureAppointments().then(response => {
        this.isFutureAppointmentsLoaded = true;
        this.futureAppointments = response.data;
      });
    },
    sendFetchPastAppointments() {
      return this.fetchPastAppointments().then(() => {
        this.isPastAppointmentsLoaded = true;
      });
    },
    sendCanceledAppointments() {
      return this.fetchCanceledAppointments().then(resp => {
        this.isCanceledAppointmentsLoaded = true;
      });
    },
    copyToClipboard() {
      navigator.clipboard.writeText(this.nextAppointmentLink);
    },
    getNextAppointmentId() {
      if (this.nextAppointment && this.nextAppointment.id)
        return `#${this.nextAppointment.id.split("-")[0]}`.toUpperCase();

      return "";
    },
    getNextAppointmentPatient() {
      return this.nextAppointment && this.nextAppointment.id
        ? this.nextAppointment.patient.name
        : "";
    },
    joinRoom(room) {
      this.$router.push({ name: "WaitingRoom", params: { room: room.id } });
    },

    joinCreatedRoom(appointment) {
      this.joinRoom(appointment.room);
    },

    callCreateRoom(room) {
      this.createRoomV2(room.appointment_id).then(response => {
        this.appointments[this.nextAppointmentIndex].room = response.data;
        if (this.nextAppointment.id) this.nextAppointment.room = response.data;
        this.nextAppointmentHasRoom = true;
      });
    },

    appointmentCanceled() {
      this.isBusy = true;
      this.isFutureAppointmentsLoaded = false;
      this.sendFetchAppointments();
      this.sendFetchFutureAppointments();
    },
    // A consulta encerrada some de "Próximas" e passa para "Realizadas" —
    // recarrega as duas listas.
    appointmentFinished() {
      this.isBusy = true;
      this.isFutureAppointmentsLoaded = false;
      this.isPastAppointmentsLoaded = false;
      this.sendFetchAppointments();
      this.sendFetchFutureAppointments();
      this.sendFetchPastAppointments();
    },
    appointmentRoomCreated(appointment) {
      this.createRoomV2(appointment.id).then(response => {
        this.isFutureAppointmentsLoaded = false;

        if (this.appointments[this.nextAppointmentIndex].id == appointment.id) {
          this.appointments[this.nextAppointmentIndex].room = response.data;
          if (this.nextAppointment.id)
            this.nextAppointment.room = response.data;
          this.nextAppointmentHasRoom = true;
        }

        this.fetchFutureAppointments().then(response => {
          this.futureAppointments = response.data;
          this.isFutureAppointmentsLoaded = true;
        });
      });
    },

    copyAppointmentRoomLink(appointment) {
      const roomUrl = `${window.location.origin}/consultorio/${appointment.room.id}`;
      navigator.clipboard.writeText(roomUrl);
    },
    setSelectedTab(tab) {
      this.selected = tab;
    },
    avatarInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .slice(0, 2)
        .map(n => n[0])
        .join("")
        .toUpperCase();
    },
    tableConfig(type) {
      if (this.userIsDoctor) return this.doctorTableConfig;
      if (this.userIsReceptionist) return this.receptionistTableConfig(type);
      return this.patientTableConfig;
    },
    async onAuditTabClick() {
      this.activeTab = 3;
      if (this.auditLog.length > 0) return;
      const response = await Doctor.auditLog();
      this.auditLog = response.data;
    },
    actionLabel(action) {
      const labels = {
        appointment_cancelled: "Consulta cancelada",
        appointment_created: "Agendamento criado"
      };
      return labels[action] || action;
    },
    actionBadgeClass(action) {
      const classes = {
        appointment_cancelled: "badge--danger",
        appointment_created: "badge--success"
      };
      return classes[action] || "badge--neutral";
    },
    formatAuditDate(iso) {
      if (!iso) return "";
      const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
      if (!match) return iso;
      const [, year, month, day, hours, minutes] = match;
      return `${day}/${month}/${year} às ${hours}:${minutes}`;
    },
    seeMoreAuditEntries() {
      if (this.auditDataLengthDiffer < this.auditAddPerPage) {
        this.auditPerPage += this.auditDataLengthDiffer;
      } else if (this.auditPerPage < this.auditLog.length) {
        this.auditPerPage += this.auditAddPerPage;
      }
    },
    hideAllAuditEntries() {
      this.auditPerPage = 5;
      this.auditAddPerPage = 10;
    },
    receptionistTableConfig(type) {
      const config = [
        { key: "name", label: "paciente" },
        { key: "doctor_name", label: "doutor" },
        { key: "formated_scheduled_at", label: "data" },
        { key: "formated_scheduled_at_time", label: "hora" },
        { key: "status", label: "status" },
        { key: "med_record", label: "ficha" }
      ];

      if (type === "future")
        config.push({ key: "receptionist_actions", label: "ações" });

      return config;
    }
  },
  computed: {
    ...userComputed,
    pagedAuditLog() {
      return this.auditLog.slice(0, this.auditPerPage);
    },
    showingAuditPerPage() {
      return this.auditLog.length > 5
        ? this.auditPerPage
        : this.auditLog.length;
    },
    auditDataLengthDiffer() {
      return this.auditLog.length - this.auditPerPage;
    },
    showedAuditPerPageValue() {
      if (this.auditLog.length && this.auditLog.length >= 5) {
        return this.auditDataLengthDiffer < this.auditAddPerPage
          ? this.auditDataLengthDiffer
          : this.auditAddPerPage;
      }
      return 0;
    },
    showMoreAuditButton() {
      return (
        this.auditLog.length &&
        this.auditLog.length > 5 &&
        this.showedAuditPerPageValue > 0
      );
    },
    showHideAuditButton() {
      return (
        this.auditLog.length &&
        this.auditLog.length > 5 &&
        this.showedAuditPerPageValue === 0
      );
    },
    countDown() {
      if (this.nextAppointment && this.nextAppointment.scheduled_at)
        return formatDistanceToNow(
          parseISO(this.nextAppointment.scheduled_at),
          { locale }
        );

      return "";
    },
    nextAppointmentLink() {
      const route = this.$router.resolve({
        name: "RoomView",
        params: { room: this.nextAppointment.room.id }
      });

      return `${window.location.origin}${route.href}`;
    },
    changeCreateRoomInitialLabelByUserRole() {
      return this.userIsDoctor
        ? "Criar sala"
        : "Aguarde o profissional criar a sala";
    },
    changeCreateRoomLabelIfRoomExist() {
      return this.nextAppointmentHasRoom === true
        ? "Entrar na sala"
        : this.changeCreateRoomInitialLabelByUserRole;
    },
    disableButtonIfRoomNotExist() {
      return this.changeCreateRoomLabelIfRoomExist ===
        "Aguarde o profissional criar a sala"
        ? "disabled"
        : "";
    },
    appointmentsAmount() {
      return this.appointments.length;
    },
    onlyScheduledAppointments() {
      return this.appointments.filter(appointment => {
        if (!appointment.appointment_cancelation) return appointment;
      });
    },
    nextAppointmentIndex() {
      if (!this.appointments.length) return {};
      const now = new Date();
      const appointmentsDates = this.onlyScheduledAppointments.map(
        appointment => parseISO(appointment.scheduled_at)
      );
      return closestIndexTo(now, appointmentsDates);
    },
    nextAppointment() {
      if (!this.appointments.length) return {};
      const nextAppointment = this.onlyScheduledAppointments[
        this.nextAppointmentIndex
      ];

      if (!nextAppointment) return {};

      nextAppointment.buttons = [];
      if (nextAppointment.room && nextAppointment.room.id) {
        const joinRoom = {
          buttonText: "Entrar na sala",
          buttonAction: "joinRoom",
          buttonPayload: nextAppointment.room
        };

        const copyToClipboard = {
          buttonText: "",
          buttonIcon: true,
          buttonAction: "copyToClipboard",
          buttonPayload: ""
        };

        nextAppointment.buttons.push(joinRoom);
        nextAppointment.buttons.push(copyToClipboard);
      } else {
        const createRoom = {
          buttonText: "Criar sala virtual",
          buttonAction: "callCreateRoom",
          buttonPayload: nextAppointment.id
        };

        if (this.userIsDoctor) nextAppointment.buttons.push(createRoom);
      }

      return nextAppointment;
    },
    nextPatientName() {
      return this.nextAppointment.patient
        ? this.nextAppointment.patient.name
        : "";
    },
    nextDoctorName() {
      return this.nextAppointment.doctor
        ? this.nextAppointment.doctor.name
        : "";
    },
    nextPatientPhoto() {
      return this.nextAppointment.patient
        ? this.nextAppointment.patient.photo
        : "";
    },
    nextDoctorPhoto() {
      return this.nextAppointment.doctor
        ? this.nextAppointment.doctor.photo
        : "";
    },
    photoFallback() {
      if (this.userIsDoctor) {
        return this.nextPatientPhoto == null
          ? require("../assets/images/doctor-img.jpg")
          : this.nextPatientPhoto;
      }
      if (this.userIsPatient) {
        return this.nextDoctorPhoto == null
          ? require("../assets/images/doctor-img.jpg")
          : this.nextDoctorPhoto;
      }

      return require("../assets/images/doctor-img.jpg");
    },
    nextAppointmentCaption() {
      return this.userIsDoctor ? "Próximo paciente" : "Próxima consulta";
    },
    availableBalance() {
      return this.$store.state.doctor.balances.current[0].amount;
    },
    progressBar() {
      return `stroke-dashoffset: calc(205 - (205 * 75) / 100);`;
    },
    cardLength() {
      return this.nextAppointment?.buttons?.length ? "mb-3" : "mb-6";
    },
    todayAppointments() {
      return this.$store.getters["appointments/todayAppointments"];
    },
    scheduledAppointments() {
      return this.$store.getters["appointments/scheduledAppointments"];
    },
    pastAppointments() {
      return this.$store.getters["appointments/pastAppointments"];
    },
    canceledAppointments() {
      return this.$store.getters["appointments/canceledAppointments"];
    },
    userIsDoctor() {
      return isDoctor(this.currentUser);
    },
    userIsPatient() {
      return isPatient(this.currentUser);
    },
    userIsReceptionist() {
      return isReceptionist(this.currentUser);
    },
    currentUserEmail() {
      return this.currentUser.email;
    },
    greetingWithName() {
      const h = new Date().getHours();
      const greeting = h < 12 ? "Bom dia" : h < 18 ? "Boa tarde" : "Boa noite";
      const name = stripHonorific(this.currentUser?.name);
      if (!name) return greeting;
      const prefix = this.userIsDoctor ? "Dr(a). " : "";
      return `${greeting}, ${prefix}${name}`;
    },
    todayFormatted() {
      try {
        return format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale });
      } catch {
        return "";
      }
    }
  },
  watch: {
    nextAppointment() {
      if (this.countDownToNextAppointment == 0) {
        this.countDownToNextAppointment = differenceInSeconds(
          parseISO(this.nextAppointment.scheduled_at),
          new Date()
        );
      }
    },
    countDownToNextAppointment(newVal) {
      if (newVal > 0) {
        setTimeout(() => (this.countDownToNextAppointment -= 1), 1000);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.db-content {
  padding: var(--space-6) var(--space-8) var(--space-12);
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  max-width: 1280px;
  width: 100%;
}

// ── Métricas ──────────────────────────────────────────────────
.metrics4 {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-4);

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.metric-card {
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  text-decoration: none;

  &--interactive {
    cursor: pointer;
    transition: box-shadow 0.15s;
    &:hover {
      box-shadow: var(--shadow-md);
    }
  }
}

.metric-icon {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-1);
}

.metric-label {
  font-size: var(--font-size-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.metric-value {
  font-size: var(--font-size-3xl);
  font-weight: var(--fw-bold);
  color: var(--color-gray-900);
  font-family: var(--font-num);
  line-height: 1.1;
}

// ── Próxima consulta ──────────────────────────────────────────
.db-tabs-header {
  padding: var(--space-5) var(--space-6) 0;
}

.db-next-card {
  padding: var(--space-5);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-5);

  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.db-next-info {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  flex: 1;
  min-width: 0;
}

.db-next-text {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
}

.db-next-label {
  font-size: var(--font-size-xs);
  font-weight: var(--fw-semibold);
  color: var(--color-gray-400);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.db-next-name {
  font-size: var(--font-size-lg);
  font-weight: var(--fw-bold);
  color: var(--color-gray-900);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.db-next-countdown {
  font-size: var(--font-size-xs);
  color: var(--color-success);
}

.db-metric-link {
  font-size: var(--font-size-xs);
  color: var(--color-primary);
  font-weight: var(--fw-bold);
  margin-top: auto;
}

.audit-log-panel {
  &__empty {
    padding: var(--space-4) var(--space-6) var(--space-6);
    color: var(--color-gray-400);
    font-size: var(--font-size-sm);
    margin: 0;
  }
}

.audit-log-table-wrap {
  padding: var(--space-4) var(--space-6);
  overflow-x: auto;
}

.audit-log-table {
  width: 100%;
  border-collapse: collapse;
  font-family: var(--font-sans, "Nunito Sans", sans-serif);

  thead th {
    font-size: var(--font-size-xs);
    font-weight: var(--fw-bold);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--color-gray-400);
    text-align: left;
    padding: var(--space-3) var(--space-4);
    background: var(--color-bg-page);
    white-space: nowrap;
  }

  tbody tr {
    border-bottom: 1px solid var(--color-gray-100);
    transition: background 0.1s;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: var(--color-bg-page);
    }
  }

  tbody td {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-800);
    padding: var(--space-4);
    vertical-align: middle;
  }

  &__date {
    font-family: var(--font-num);
    color: var(--color-gray-700);
    white-space: nowrap;
  }
}

.at-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-6);
  border-top: 1px solid var(--color-gray-100);
}

.at-footer-count {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);

  b {
    color: var(--color-gray-800);
  }
}

.at-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-1);
  font-family: var(--font-sans, "Nunito Sans", sans-serif);
  font-size: var(--font-size-sm);
  font-weight: var(--fw-bold);
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;

  &--link {
    color: var(--color-primary);

    svg {
      transition: transform 0.15s;
    }

    &:hover {
      color: var(--color-primary-dark, #1456c6);

      svg {
        transform: translateX(3px);
      }
    }
  }
}
</style>
